// Single source of truth for slug-level RBAC. Mirrors the RoleRoute allow-lists
// in App.jsx. Used to (a) filter the nav menu so users only see what they can
// open, (b) gate the Home module grid, and (c) route each role to a useful
// landing dashboard. Keep in sync with App.jsx route guards.

import { ROLES } from './roles.js';

const {
  OPERATOR_ADMIN,
  REGIONAL_ADMIN,
  FINANCE_USER,
  MERCHANT_USER,
  WAREHOUSE_OPERATOR,
  CROSS_BORDER_OPS,
} = ROLES;

// slug → roles allowed to access it.
export const SLUG_ROLES = Object.freeze({
  // Role overview dashboards (landing pages)
  'finance-overview': [FINANCE_USER, OPERATOR_ADMIN],
  'warehouse-overview': [WAREHOUSE_OPERATOR, OPERATOR_ADMIN],
  'regional-overview': [REGIONAL_ADMIN, OPERATOR_ADMIN],
  // Operator Admin — warehouse config
  'warehouse-location-designer-bin-configurator': [OPERATOR_ADMIN],
  'storage-location-occupancy-monitor': [OPERATOR_ADMIN],
  // Channel integrations
  'channel-integration-catalog': [OPERATOR_ADMIN, MERCHANT_USER],
  // Cross-border
  'cross-border-logistics-dashboard': [CROSS_BORDER_OPS, OPERATOR_ADMIN],
  // Merchant dashboards (+ operator oversight)
  'merchant-orders-dashboard': [MERCHANT_USER, OPERATOR_ADMIN],
  'merchant-cod-collections': [MERCHANT_USER, OPERATOR_ADMIN],
  'client-analytics-dashboard': [MERCHANT_USER, OPERATOR_ADMIN],
  'regional-merchant-performance': [MERCHANT_USER, OPERATOR_ADMIN],
  // Operator admin — couriers / routing / sync
  'courier-settings-service-zones': [OPERATOR_ADMIN],
  'order-routing-rules-setup': [OPERATOR_ADMIN],
  'integration-sync-api-monitor': [OPERATOR_ADMIN],
  // Regional
  'country-operations-profile': [REGIONAL_ADMIN, OPERATOR_ADMIN],
  // Finance (+ operator oversight)
  'regional-tax-and-currency-manager': [OPERATOR_ADMIN, FINANCE_USER],
  'client-billing-rule-configurator': [OPERATOR_ADMIN, FINANCE_USER],
  'cod-remittance-uploader': [OPERATOR_ADMIN, FINANCE_USER],
  'cod-discrepancy-reconciliation-worklist': [OPERATOR_ADMIN, FINANCE_USER],
  'billing-e-invoices-gate': [OPERATOR_ADMIN, FINANCE_USER],
  'courier-rts-transit-damage-claims-ledger': [OPERATOR_ADMIN, FINANCE_USER],
  // Warehouse operator
  'inbound-po-receiving-qa-terminal': [WAREHOUSE_OPERATOR],
  'warehouse-orders-queue': [WAREHOUSE_OPERATOR, OPERATOR_ADMIN],
  'directed-putaway-stock-movement-monitor': [WAREHOUSE_OPERATOR],
  'wave-batch-picking-console': [WAREHOUSE_OPERATOR, OPERATOR_ADMIN],
  'pick-queue-execution': [WAREHOUSE_OPERATOR, OPERATOR_ADMIN],
  'packing-station-terminal': [WAREHOUSE_OPERATOR, OPERATOR_ADMIN],
  'returns-intake-inspection-terminal': [WAREHOUSE_OPERATOR, OPERATOR_ADMIN],
  'replenishment-min-max-planner': [OPERATOR_ADMIN, WAREHOUSE_OPERATOR],
  'cycle-count-inventory-adjustment-console': [WAREHOUSE_OPERATOR, FINANCE_USER, OPERATOR_ADMIN],
  'bin-transfer-adjustment-terminal': [WAREHOUSE_OPERATOR, OPERATOR_ADMIN],
});

/** Can `role` open `slug`? Slugs with no entry are open to any authenticated user. */
export function canAccess(slug, role) {
  const allowed = SLUG_ROLES[slug];
  if (!allowed) return true;
  return !!role && allowed.includes(role);
}

/** The landing dashboard each role should be routed to from the app root. */
export const ROLE_LANDING = Object.freeze({
  [OPERATOR_ADMIN]: 'client-analytics-dashboard',
  [MERCHANT_USER]: 'client-analytics-dashboard',
  [CROSS_BORDER_OPS]: 'cross-border-logistics-dashboard',
  [FINANCE_USER]: 'finance-overview',
  [WAREHOUSE_OPERATOR]: 'warehouse-overview',
  [REGIONAL_ADMIN]: 'regional-overview',
});

export function landingFor(role) {
  return ROLE_LANDING[role] || null;
}

export default SLUG_ROLES;
